import { useMemo, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { CaretLeft, MagnifyingGlass } from "@phosphor-icons/react";
import { Step } from "./constants";
import { Input } from "@/components/ui/input";
import { DrawerHeader } from "@/components/ui/drawer";
import { useTranslation } from "react-i18next";
import { Currency, Vessel } from "@/lib/types";
import Loading from "@/components/custom/loading";
import { useGetExchangeRatesByDataRange } from "@/services/queries/exchange-rates";
import { addDays, format } from "date-fns";
import { currencyFormatter } from "@/lib/utils";
import { ExpenseFormValues } from "./";

const currencyIcons = {
  EUR: "/svgs/europe.svg",
  USD: "/svgs/usa.svg",
  GBP: "/svgs/england.svg",
  TRY: "/svgs/turkey.svg",
  BTC: "/svgs/btc.svg",
};

const CurrencyStep = ({
  currencies,
  isCurrenciesPending,
  form,
  setStep,
  vessels,
}: {
  currencies: Currency[];
  isCurrenciesPending: boolean;
  form: UseFormReturn<ExpenseFormValues>;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  vessels: Vessel[];
}) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const { data: exchangeRatesData } = useGetExchangeRatesByDataRange(
    format(form.watch("transactionDate") ?? new Date(), "yyyy-MM-dd"),
    format(
      addDays(form.watch("transactionDate") ?? new Date(), 1),
      "yyyy-MM-dd"
    )
  );

  const exchangeRates = useMemo(
    () => exchangeRatesData?.value ?? [],
    [exchangeRatesData]
  );

  const vessel = useMemo(
    () => vessels.find((vessel) => vessel.id === form.watch("vesselId")),
    [vessels, form]
  );

  const vesselExchangeRates = useMemo(() => {
    return exchangeRates.filter(
      (rate) => rate.fromCurrencyId === vessel?.defaultCurrencyId
    );
  }, [exchangeRates, vessel]);

  const currenciesWithExchangeRates = useMemo(() => {
    return currencies.map((currency) => {
      const exchangeRate = vesselExchangeRates.find(
        (rate) => rate.toCurrencyId === currency.id
      );

      return {
        ...currency,
        exchangeRate: currencyFormatter(
          currency.id === vessel?.defaultCurrencyId
            ? 1
            : exchangeRate?.rate
              ? exchangeRate.rate
              : undefined,
          exchangeRate?.rate
            ? exchangeRate?.toCurrencyCode
            : vessel?.defaultCurrencyCode
        ),
      };
    });
  }, [
    currencies,
    vessel?.defaultCurrencyCode,
    vessel?.defaultCurrencyId,
    vesselExchangeRates,
  ]);

  const filteredCurrencies = currenciesWithExchangeRates.filter((currency) =>
    t(`currency.${currency.code.toLowerCase()}`)
      .toLocaleLowerCase()
      .includes(search.toLocaleLowerCase())
  );

  return (
    <div>
      <DrawerHeader
        title={t("title.select_currency")}
        leftButton={
          <button type="button" onClick={() => setStep(Step.INITAL)}>
            <CaretLeft color="#E3EDFC" size={18} />
          </button>
        }
        rightButton={<span className="w-[18px]" />}
      />
      <div className="relative mt-5 w-full">
        <MagnifyingGlass
          color="#719BAD"
          className="pointer-events-none absolute left-2 top-1/2 z-10 -translate-y-1/2"
          size={20}
        />
        <Input
          className="pl-10"
          placeholder={t("label.search")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {isCurrenciesPending ? (
        <Loading className="mt-10" />
      ) : (
        <div className="mt-5 flex flex-col items-center justify-center">
          {filteredCurrencies.map((currency) => (
            <button
              data-testid={`expense-form-currency-item-${currency.code}`}
              type="button"
              onClick={() => {
                form.setValue("currencyId", currency.id);
                setStep(Step.INITAL);
              }}
              key={currency.id}
              className="flex w-full flex-row items-center justify-between gap-[12px] border-b border-white/15 py-4 text-base text-white"
            >
              <span className="flex flex-row items-center gap-[12px]">
                <img
                  src={
                    currencyIcons[currency.code as keyof typeof currencyIcons]
                  }
                  alt={currency.name}
                />
                {t(`currency.${currency.code.toLowerCase()}`)}
              </span>
              <span className="text-[15px] font-medium text-white">
                {currency.exchangeRate}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default CurrencyStep;
